import type {
  BlockCheckPayload,
  ParticipantPayload,
  TrialResponsePayload
} from "../types/study";

type ApiResult = {
  ok: boolean;
  message?: string;
};

function isLocalFallbackAllowed(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
}

async function postJson(path: string, payload: unknown): Promise<ApiResult> {
  try {
    const response = await fetch(path, {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      return { ok: true };
    }

    return {
      ok: false,
      message: `${path} failed with status ${response.status}`
    };
  } catch {
    return {
      ok: false,
      message: `${path} could not be reached`
    };
  }
}

function saveLocal(kind: string, payload: unknown): void {
  const key = `socialgo:${kind}`;
  const existing = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown[];
  existing.push(payload);
  localStorage.setItem(key, JSON.stringify(existing));
}

export async function saveParticipant(payload: ParticipantPayload): Promise<void> {
  const result = await postJson("/api/participants", payload);
  if (!result.ok) {
    if (!isLocalFallbackAllowed()) {
      throw new Error(result.message ?? "Failed to save participant");
    }
    saveLocal("participants", payload);
  }
}

export async function saveTrialResponse(payload: TrialResponsePayload): Promise<void> {
  const result = await postJson("/api/responses", payload);
  if (!result.ok) {
    if (!isLocalFallbackAllowed()) {
      throw new Error(result.message ?? "Failed to save trial response");
    }
    saveLocal("trial-responses", payload);
  }
}

export async function saveBlockCheck(payload: BlockCheckPayload): Promise<void> {
  const result = await postJson("/api/block-checks", payload);
  if (!result.ok) {
    if (!isLocalFallbackAllowed()) {
      throw new Error(result.message ?? "Failed to save block check");
    }
    saveLocal("block-checks", payload);
  }
}

export async function completeParticipant(participantId: string): Promise<string> {
  const fallback = `SG-${participantId.slice(-8).toUpperCase()}`;
  try {
    const response = await fetch("/api/complete", {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({ participant_id: participantId })
    });
    if (!response.ok) {
      if (!isLocalFallbackAllowed()) {
        throw new Error(`/api/complete failed with status ${response.status}`);
      }
      return fallback;
    }
    const payload = (await response.json()) as { completion_code?: string };
    return payload.completion_code ?? fallback;
  } catch {
    if (!isLocalFallbackAllowed()) {
      throw new Error("Could not reach /api/complete");
    }
    return fallback;
  }
}
